import { useAuthorization } from 'dx-sdk/build/authorization';
import {
  Accordion,
  Button,
  Hero,
  IconCard,
  ItemsGrid,
  LinkArrow,
  NextStepCard,
  Picture,
  PlannerField,
  PlannerFields,
  PlannerQuestion,
  PlannerQuestionnaire,
  Typography,
} from 'dx-sdk/build/components';
import RadioQuestion from 'dx-sdk/build/components/PlannerQuestion/components/Radio';
import { Arrow, Pencil, PencilAndPaper } from 'dx-sdk/build/icons';
import { Theme as ThemeProvider } from 'dx-sdk/build/providers';
import { formatDealerName } from 'pubweb-smokey/dist/utils/formatters';
import { runCommonValidation } from 'pubweb-smokey/dist/utils/validation';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { InView } from 'react-intersection-observer';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import App from '../../../App';
import { smoothScroll } from '../../../components/Shared/SmokeyTransitional/utils/utils';
import { DXAccountContext } from '../../../contexts/DXAccountContext';
import useWindowSize from '../../../hooks/useWindowSize';
import article from '../../../images/portal/icons/article.svg';
import lightbulb from '../../../images/portal/icons/lightbulb.svg';
import money from '../../../images/portal/icons/money.svg';
import PlannerLandHeroBG from '../../../images/portal/planner/land/PlannerLandHeroBG.jpg';
import PreferencesNextStepIcon from '../../../images/portal/planner/preferences/PreferencesNextStepIcon.svg';
import Loading from '../../../images/svg/icons/Loading';
import * as dealerService from '../../../services/dealerService';
import * as homesService from '../../../services/homesService';
import { fireSegmentEvent, parseInitialPropsContext } from '../../../utils';
import LocationTextBox from '../../components/LocationTextBox/LocationTextBox';
import LandIcon from '../../components/PlannerIcons/LandIcon';
import RecommendedHomes from '../../components/RecommendedHomes/RecommendedHomes';
import {
  findLandHomes,
  landAnswer,
  portalIconProgress,
} from '../../utils/PortalUtils';
import LandStyles from './Land.styled';

const Land = ({ dealer, homes }) => {
  const dxContext = useContext(DXAccountContext);
  const [activeQuestion, setActiveQuestion] = useState(1);
  const [landButtonDisabled, setLandButtonDisabled] = useState(false);
  const { isAuthenticated, isLoading } = useAuthorization();
  const [formIsSaved, setFormIsSaved] = useState(false);
  const [zipIsValid, setZipIsValid] = useState('false');
  const [allowZipUpdate, setAllowZipUpdate] = useState(false);
  const [updatedUserProfile, setUpdatedUserProfile] = useState(null);
  const [updatedUser, setUpdatedUser] = useState({
    userId: '',
  });
  const [width, height] = useWindowSize();
  const [newPostalCode, setNewPostalCode] = useState(null);
  const [formErrors, setFormErrors] = useState(
    runCommonValidation({
      zip: {
        value: newPostalCode ? newPostalCode : ' ',
        touched: true,
        notRequired: false,
      },
    })
  );
  const location = useLocation();
  const history = useHistory();

  const [landFormSavedHCs, setLandFormSavedHCs] = useState([]);
  const [landFormSavedProfileIds, setLandFormSavedProfileIds] = useState([]);

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem('land_fs'));
    if (items) {
      setLandFormSavedHCs(items.homeCenters);
      setLandFormSavedProfileIds(items.profileIds);
    }
  }, []);

  const isMobile = width <= 285;

  useEffect(() => {
    if (landFormSavedHCs && landFormSavedHCs.length) {
      let filteredHCList = [...new Set(landFormSavedHCs)];
      let filteredProfileIdList = [...new Set(landFormSavedProfileIds)];
      localStorage.setItem(
        'land_fs',
        JSON.stringify({
          homeCenters: filteredHCList,
          profileIds: filteredProfileIdList,
        })
      );
    }
  }, [landFormSavedHCs, landFormSavedProfileIds]);

  useEffect(() => {
    let lotsStored =
      landFormSavedHCs && landFormSavedHCs.length
        ? landFormSavedHCs?.map((i) => i)
        : [];
    let profileIdsStored =
      landFormSavedProfileIds && landFormSavedProfileIds.length
        ? landFormSavedProfileIds?.map((i) => i)
        : [];
    if (
      !location?.state?.activeQuestionLink &&
      lotsStored.includes(dealer?.dealerId) &&
      profileIdsStored.includes(updatedUserProfile?.profileId)
    ) {
      setFormIsSaved(true);
    }
  }, [
    landFormSavedHCs,
    landFormSavedProfileIds,
    updatedUserProfile?.profileId,
  ]);

  useEffect(() => {
    if (location?.state?.activeQuestionLink) {
      history.replace({ state: { dealer, homes } }) +
        setFormIsSaved(false) +
        setActiveQuestion(location?.state?.activeQuestionLink) +
        smoothScroll('#plannerSection', -20);
    }
  }, []);

  const landPlannerProgressObj =
    dxContext?.state?.account?.plannerProgress?.find(
      (item) => item.section == 'Land'
    );

  const portalIcon = portalIconProgress(
    landPlannerProgressObj?.percentCompleted || 0
  );

  const onPostalCodeChange = (value) => {
    setAllowZipUpdate(true);
    setNewPostalCode(value);
  };

  useEffect(() => {
    if (
      dxContext?.state?.account &&
      dxContext?.state?.account?.profile &&
      !updatedUser.userId
    ) {
      setUpdatedUserProfile(dxContext?.state?.account?.profile);
      setUpdatedUser({
        userId: dxContext?.state?.account.userId,
      });
    }
  }, [dxContext?.state?.account]);

  const BackLink = () => {
    return (
      <LinkArrow
        isReversed
        components={{ Root: Link }}
        componentProps={{
          to: { pathname: '/portal/planner', state: { dealer, homes } },
        }}
      >
        Back to MyHome Planner
      </LinkArrow>
    );
  };

  const PreferencesLink = ({ children }) => {
    return (
      <NextStepCard
        {...children}
        components={{
          Link: () => (
            <Link
              className="NextStepCard-link"
              to={{
                pathname: '/portal/my-home-aspirations',
                state: { dealer, homes },
              }}
            >
              <Arrow />
            </Link>
          ),
        }}
      />
    );
  };

  const recommendedHomes = findLandHomes(homes);

  const homesList = useMemo(() => {
    return recommendedHomes
      ? recommendedHomes.map((item) => {
          return {
            id: item.serialNumber || item.modelNumber,
            retailHomeObject: {
              ...item,
            },
          };
        })
      : null;
  }, [recommendedHomes]);

  const CustomAction = (value, questionNumber) => {
    return (
      <div
        className="DXButton DXButton-root"
        onClick={() => {
          setFormIsSaved(false) + setActiveQuestion(questionNumber);
        }}
      >
        {value || updatedUserProfile?.hasLand !== null ? (
          <>
            <Pencil /> <span className="hide-on-mobile">Edit</span>
          </>
        ) : (
          <>
            <PencilAndPaper />
            <span className="hide-on-mobile">COMPLETE YOUR ANSWER</span>
          </>
        )}
      </div>
    );
  };

  const accordionContent = [
    {
      title: 'Zoning',
      id: 'zoning',
      content: (
        <Typography>
          Even if you have potentially found the perfect piece of land, you will
          need to confirm that it is zoned for manufactured homes. Be sure to
          check the zoning regulations in the area where you want to live. Also,
          confirm that there are no subdivision covenants that restrict
          manufactured homes.
        </Typography>
      ),
    },
    {
      title: 'Site Preparation',
      id: 'site_preparation',
      content: (
        <Typography>
          Your local home consultant will help guide you through site
          preparation and help you find local contractors, but it is good to
          know what to expect. You will need to determine the land’s soil
          conditions to make sure it is suitable for the home and determine the
          location of the home on the site. Plan to clear the area of foliage
          and, if necessary, grade the home site for proper drainage. While the
          site is being prepared, you will also want to think about land
          improvement options such as electrical services, septic system
          installation, creating a driveway and possibly having a garage, deck
          or porch built.
        </Typography>
      ),
    },
    {
      title: 'Foundations',
      id: 'foundations',
      content: (
        <Typography>
          Manufactured homes can be placed on a variety of foundations. Your
          budget, timeline and personal preferences can determine which
          foundation is right for you. Pier foundations are appropriate for both
          single and multi-section homes and are generally enclosed in vinyl
          skirting. Slab foundations are often poured over piers to add
          stability through freezes and thaws. Slab foundations with concrete
          block skirting, crawl space foundations and basement foundations give
          the homes a more residential look as well as room for storage and
          utilities.
        </Typography>
      ),
    },
  ];

  const handleProfileFieldUpdate = (key) => (value) => {
    const newProfileObj = {
      ...updatedUserProfile,
      hasLand: value === 'owned' || value === 'family' ? true : false,
      [key]: value,
    };

    dxContext?.actions?.markContentComplete(
      landPlannerProgressObj,
      'land_question_1'
    );
    setUpdatedUserProfile(newProfileObj);
    dxContext?.actions?.updateProfileRetail(newProfileObj);
  };

  useEffect(() => {
    if (allowZipUpdate) {
      let zip = newPostalCode;
      let zipInt = parseFloat(zip) || null;

      var formErrors = runCommonValidation({
        zip: { value: zip, touched: true },
      });

      const newZipObj = {
        ...updatedUserProfile,
        landZip: zipInt,
      };

      if (zipInt === null) {
        setUpdatedUserProfile(newZipObj);
        dxContext?.actions?.updateProfileRetail(newZipObj);
        setFormErrors(null);
        dxContext?.actions?.markContentIncomplete(
          landPlannerProgressObj,
          'land_question_2'
        );
      } else if (formErrors.hasErrors) {
        setFormErrors(formErrors);
        // dxContext?.actions?.markContentIncomplete(
        //   landPlannerProgressObj,
        //   'land_question_2'
        // );
      } else {
        setUpdatedUserProfile(newZipObj);
        dxContext?.actions?.updateProfileRetail(newZipObj);
        dxContext?.actions?.markContentComplete(
          landPlannerProgressObj,
          'land_question_2'
        );
        setFormErrors(null);
      }
    }
  }, [newPostalCode, allowZipUpdate]);

  useEffect(() => {
    const numbersOnlyRegex = /^-?\d+\.?\d*$/;
    if (activeQuestion === 2) {
      if (
        newPostalCode &&
        newPostalCode.length > 0 &&
        (formErrors || !numbersOnlyRegex.test(newPostalCode))
      ) {
        setLandButtonDisabled(true);
      } else {
        setLandButtonDisabled(false);
      }
    } else {
      setLandButtonDisabled(false);
    }
  }, [activeQuestion, newPostalCode, formErrors]);

  useEffect(() => {
    setFormErrors(null);
  }, [updatedUserProfile?.landZip]);

  if (!isAuthenticated && !isLoading) {
    return <Redirect to="/myhome-account-login" />;
  }

  return (
    <ThemeProvider theme={{}}>
      <App dealer={dealer} homes={homes}>
        <LandStyles>
          <Helmet>
            <title>Land | {formatDealerName(dealer?.dealerName)}</title>
            <meta name="robots" content="noindex" />
          </Helmet>

          {dxContext?.state?.globalLoading ? (
            <div className="loading-screen-panel">
              <Loading width="150px" />
            </div>
          ) : (
            <>
              <Hero
                components={{
                  Backdrop: Picture,
                }}
                componentsProps={{
                  Backdrop: {
                    src: PlannerLandHeroBG,
                  },
                  Container: {
                    style: {
                      margin: '0px auto',
                    },
                  },
                }}
              >
                <IconCard
                  heading="Learn About Land"
                  icon={
                    <LandIcon
                      dashArray={portalIcon?.dashArray}
                      dashOffset={portalIcon?.dashOffset}
                      opacity={portalIcon?.opacity}
                      showStar={portalIcon?.showStar}
                    />
                  }
                  subheading="Back to MyHome Planner"
                  iconOverlay
                  components={{
                    Subheading: BackLink,
                  }}
                >
                  <Typography>
                    Land status can impact your timeline and overall costs. Know
                    the right questions to ask, what costs to expect and what is
                    required during site preparation. If you are looking to move
                    in quickly, contact {formatDealerName(dealer?.dealerName)}{' '}
                    today to discuss your options.
                  </Typography>
                </IconCard>
              </Hero>
              <section className="recommendations-section">
                <div className="container">
                  <ItemsGrid
                    heading="Recommendations to explore on this page:"
                    items={[
                      {
                        children: (
                          <span
                            onClick={() => smoothScroll('#plannerSection', -20)}
                          >
                            <img src={money} alt="money icon" />
                            Tell Us About Your Land Needs
                          </span>
                        ),
                      },
                      {
                        children: (
                          <span
                            onClick={() =>
                              smoothScroll('#planAheadSection', -20)
                            }
                          >
                            <img src={article} alt="article icon" />
                            How To Plan Ahead
                          </span>
                        ),
                      },
                      {
                        children: (
                          <span
                            onClick={() =>
                              smoothScroll('#whatToKnowSection', -20)
                            }
                          >
                            <img src={lightbulb} alt="lightbulb icon" />
                            What To Know About Land
                          </span>
                        ),
                      },
                    ]}
                    components={{
                      Item: Button,
                    }}
                    componentsProps={{
                      Heading: {
                        variant: 'p',
                      },
                      Item: {
                        variant: 'outline',
                        color: '#4E5255',
                        size: 'small',
                      },
                    }}
                  />
                </div>
              </section>
              <section className="planner-section" id="plannerSection">
                <div className="container">
                  <div className="planner-box">
                    {updatedUserProfile !== null ? (
                      <>
                        {!formIsSaved ? (
                          <PlannerQuestionnaire
                            activeQuestion={activeQuestion}
                            onSave={() => {
                              setFormIsSaved(true) +
                                setLandFormSavedHCs((hc) => [
                                  ...hc,
                                  dealer?.dealerId,
                                ]) +
                                setLandFormSavedProfileIds((p) => [
                                  ...p,
                                  updatedUserProfile?.profileId,
                                ]);
                            }}
                            handlers={{
                              onClick: (e) =>
                                smoothScroll('.planner-box', -20) +
                                setActiveQuestion(activeQuestion + 1),
                              onPrevClick: (e) => {
                                setActiveQuestion(activeQuestion - 1);
                              },
                            }}
                            componentsProps={{
                              Trigger: {
                                disabled: landButtonDisabled,
                              },
                            }}
                            children={[
                              <PlannerQuestion
                                type="custom"
                                heading="Do you already own land where you are looking to place your home?"
                                note=" If not, we can help you with that piece of the process"
                              >
                                <RadioQuestion
                                  values={[
                                    {
                                      label: 'Yep, it’s mine!',
                                      value: 'owned',
                                    },
                                    {
                                      label: 'I have family land.',
                                      value: 'family',
                                    },
                                    {
                                      label: 'No - I would love some help!',
                                      value: null,
                                    },
                                  ]}
                                  defaultValue={
                                    updatedUserProfile?.hasLand !== null
                                      ? updatedUserProfile?.landType
                                      : ''
                                  }
                                  handlers={{
                                    onChange:
                                      handleProfileFieldUpdate('landType'),
                                  }}
                                />
                              </PlannerQuestion>,
                              <PlannerQuestion
                                type="custom"
                                heading={
                                  !updatedUserProfile?.hasLand
                                    ? 'Where would you like your home to be located?'
                                    : 'Where is your property located?'
                                }
                                note="Our experts can help you plan for the different property needs impacted by zoning, conditions, and environment."
                              >
                                <LocationTextBox
                                  locationKey={'header'}
                                  postalCode={updatedUserProfile?.landZip}
                                  formErrors={formErrors}
                                  onPostalCodeChange={onPostalCodeChange}
                                  zipIsValid={zipIsValid}
                                  placeholderText={
                                    isMobile
                                      ? 'Enter zip code'
                                      : 'Enter a zip code, or search by city'
                                  }
                                />
                              </PlannerQuestion>,
                            ]}
                          />
                        ) : (
                          <PlannerFields
                            heading="Your Land Needs"
                            subheading="Your Answers"
                          >
                            <PlannerField
                              heading="Land for your home"
                              answer={landAnswer(
                                updatedUserProfile?.landType,
                                updatedUserProfile?.hasLand
                              )}
                              components={{
                                Action: () =>
                                  CustomAction(updatedUserProfile?.landType, 1),
                              }}
                            />
                            <PlannerField
                              heading="Land location"
                              answer={
                                updatedUserProfile?.landZip ||
                                `You haven't set a land location`
                              }
                              components={{
                                Action: () =>
                                  CustomAction(updatedUserProfile?.landZip, 2),
                              }}
                            />
                          </PlannerFields>
                        )}
                      </>
                    ) : (
                      <div className="planner-loading-screen">
                        <Loading width="100px" />
                      </div>
                    )}
                  </div>
                </div>
              </section>
              <InView
                triggerOnce
                as="section"
                className="left-heading-section"
                id="planAheadSection"
                threshold={0.3}
                onChange={(inView) =>
                  inView
                    ? dxContext?.actions?.markContentComplete(
                        landPlannerProgressObj,
                        'land_planning_ahead'
                      )
                    : null
                }
              >
                <div className="container">
                  <div className="left-heading-component">
                    <div className="left-side">
                      <p className="left-heading">Planning Ahead</p>
                    </div>
                    <div className="right-side">
                      <div className="list-columns">
                        <div className="list-column">
                          <p>Purchasing Land:</p>
                          <ul>
                            <li>
                              Think about location. Decide if you prefer a lot
                              in a remote location, conventional neighborhood or
                              manufactured home community.
                            </li>
                            <li>
                              Decide which loan option makes sense for you—a
                              manufactured home loan with land or two separate
                              loans, one for the land and one for the home.
                            </li>
                            <li>
                              Check local zoning ordinances and land use
                              restrictions to confirm manufactured homes are
                              allowed.
                            </li>
                            <li>
                              Speak with your local home center for advice about
                              purchasing land.
                            </li>
                            <li>
                              Browse homes at your local home center that are
                              already placed on land.
                            </li>
                          </ul>
                        </div>
                        <div className="list-column">
                          <p>Already Own Land:</p>
                          <ul>
                            <li>
                              Determine what upgrades you want, such as a
                              garage, deck or porch.
                            </li>
                            <li>
                              Confirm soil conditions are correct for the home
                              and determine the home’s placement.
                            </li>
                            <li>
                              Find a local contractor to assist with site
                              preparation.
                            </li>
                            <li>
                              Clear the land of foliage and grade the site for
                              proper drainage.
                            </li>
                            <li>
                              Make sure the site is properly crowned for
                              drainage.
                            </li>
                            <li>
                              Connect utilities or install a septic system.
                            </li>
                            <li>
                              Work with your local home center to ensure your
                              site is ready for home delivery.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </InView>
              <InView
                triggerOnce
                as="section"
                className="left-heading-section"
                id="whatToKnowSection"
                threshold={0.3}
                onChange={(inView, entry) =>
                  inView
                    ? dxContext?.actions?.markContentComplete(
                        landPlannerProgressObj,
                        'land_faq'
                      )
                    : null
                }
              >
                <div className="container">
                  <div className="left-heading-component">
                    <div className="left-side">
                      <p className="left-heading">What to know about land</p>
                    </div>
                    <div className="right-side">
                      <Accordion
                        accordions={accordionContent.map((accordion) => ({
                          ...accordion,
                          onExpand: () => {
                            fireSegmentEvent('Accordion Expanded', {
                              item: accordion.title,
                              type: 'details_expanded_land_what_to_know_about_land',
                              defaultOpen: false,
                              autoClosing: false,
                              eventContext: 'portal_land_' + accordion.id || '',
                            });
                          },
                        }))}
                      />
                    </div>
                  </div>
                </div>
              </InView>
              {homesList && homesList.length > 0 && (
                <RecommendedHomes
                  swiperID="landSwiper"
                  subHeading={`Homes with land available at ${formatDealerName(
                    dealer?.dealerName
                  )}`}
                  listItems={homesList}
                  homes={homes}
                  dealer={dealer}
                  retailEventLabel="portal_land_recommended_homes"
                />
              )}
              <section className="explore-section">
                <div className="container">
                  <ItemsGrid
                    heading="Next Step"
                    items={[
                      {
                        children: {
                          icon: (
                            <img
                              src={PreferencesNextStepIcon}
                              alt="preferences next step"
                            />
                          ),
                          chip: {
                            text: 'Dream',
                            background: '#E2F7FF',
                            color: 'rgba(37, 41, 45, 0.5)',
                          },
                          heading: 'Dream about your home',
                        },
                      },
                    ]}
                    components={{
                      Item: PreferencesLink,
                    }}
                  />
                </div>
              </section>
            </>
          )}
        </LandStyles>
      </App>
    </ThemeProvider>
  );
};

Land.getInitialProps = async (ctx) => {
  return await parseInitialPropsContext(ctx, dealerService, homesService).catch(
    (e) => {
      console.error(e);
      return { redirectTo: '/error' };
    }
  );
};

export default Land;
